import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import api from '../services/api';

function Settings() {
  const { user } = useSelector((state) => state.auth);
  const [activeTab, setActiveTab] = useState('profile');
  const [users, setUsers] = useState([]);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [message, setMessage] = useState({ type: '', text: '' });
  const [showAddUser, setShowAddUser] = useState(false);
  const [newUserForm, setNewUserForm] = useState({
    username: '',
    email: '',
    password: '',
    role: 'operator'
  });
  const [userError, setUserError] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editUserForm, setEditUserForm] = useState({
    email: '',
    role: '',
    is_active: true
  });

  useEffect(() => {
    if (user?.role === 'admin') {
      loadUsers();
    }
  }, [user]);

  const loadUsers = async () => {
    try {
      const res = await api.get('/users');
      setUsers(res.data.users);
    } catch (error) {
      console.error('Failed to load users:', error);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setUserError('');
    
    try {
      await api.post('/users', newUserForm);
      setShowAddUser(false);
      setNewUserForm({ username: '', email: '', password: '', role: 'operator' });
      loadUsers();
    } catch (error) {
      setUserError(error.response?.data?.error || 'Failed to create user');
    }
  };

  const handleEditUser = (u) => {
    setEditingUser(u);
    setEditUserForm({
      email: u.email,
      role: u.role,
      is_active: u.is_active
    });
    setUserError('');
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setUserError('');
    
    try {
      await api.put(`/users/${editingUser.id}`, editUserForm);
      setEditingUser(null);
      loadUsers();
    } catch (error) {
      setUserError(error.response?.data?.error || 'Failed to update user');
    }
  };

  const handleDeleteUser = async () => {
    if (!editingUser) return;
    
    const confirmed = window.confirm(
      `Are you sure you want to permanently delete user "${editingUser.username}"? This action cannot be undone.`
    );
    
    if (!confirmed) return;
    
    setUserError('');
    
    try {
      await api.delete(`/users/${editingUser.id}`);
      setEditingUser(null);
      loadUsers();
    } catch (error) {
      setUserError(error.response?.data?.error || 'Failed to delete user');
    }
  };

  const handleToggleMFA = async () => {
    try {
      if (user?.mfa_enabled) {
        await api.post('/auth/mfa/disable');
        setMessage({ type: 'success', text: 'MFA disabled successfully' });
      } else {
        setMessage({ type: 'info', text: 'MFA setup is not yet implemented. Coming soon!' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to toggle MFA' });
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      setMessage({ type: 'error', text: 'Passwords do not match' });
      return;
    }

    try {
      await api.post('/auth/change-password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      });
      setMessage({ type: 'success', text: 'Password changed successfully' });
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (error) {
      setMessage({ type: 'error', text: error.response?.data?.error || 'Failed to change password' });
    }
  };

  return (
    <>
      <header className="main-header">
        <h1 className="page-title">Settings</h1>
      </header>

      <div className="main-body">
        {/* Tabs */}
        <div style={{ 
          display: 'flex', 
          gap: 'var(--spacing-sm)', 
          marginBottom: 'var(--spacing-xl)',
          borderBottom: '1px solid var(--border-color)',
          paddingBottom: 'var(--spacing-md)'
        }}>
          <button
            className={`btn ${activeTab === 'profile' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setActiveTab('profile')}
          >
            👤 Profile
          </button>
          <button
            className={`btn ${activeTab === 'security' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setActiveTab('security')}
          >
            🔐 Security
          </button>
          {user?.role === 'admin' && (
            <button
              className={`btn ${activeTab === 'users' ? 'btn-primary' : 'btn-ghost'}`}
              onClick={() => setActiveTab('users')}
            >
              👥 Users
            </button>
          )}
          <button
            className={`btn ${activeTab === 'about' ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setActiveTab('about')}
          >
            ℹ️ About
          </button>
        </div>

        {/* Profile Tab */}
        {activeTab === 'profile' && (
          <div className="card" style={{ maxWidth: '600px' }}>
            <div className="card-header">
              <h3 className="card-title">Profile Information</h3>
            </div>
            <div className="card-body">
              <div className="form-group">
                <label className="form-label">Username</label>
                <input
                  type="text"
                  className="form-input"
                  value={user?.username || ''}
                  disabled
                />
              </div>
              <div className="form-group">
                <label className="form-label">Email</label>
                <input
                  type="email"
                  className="form-input"
                  value={user?.email || ''}
                  disabled
                />
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <input
                  type="text"
                  className="form-input"
                  value={user?.role || ''}
                  disabled
                  style={{ textTransform: 'capitalize' }}
                />
              </div>
              <div className="form-group">
                <label className="form-label">MFA Status</label>
                <span style={{ 
                  color: user?.mfa_enabled ? 'var(--neon-green)' : 'var(--neon-orange)'
                }}>
                  {user?.mfa_enabled ? '✓ Enabled' : '✗ Disabled'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Security Tab */}
        {activeTab === 'security' && (
          <div style={{ maxWidth: '600px' }}>
            <div className="card" style={{ marginBottom: 'var(--spacing-xl)' }}>
              <div className="card-header">
                <h3 className="card-title">Change Password</h3>
              </div>
              <div className="card-body">
                {message.text && (
                  <div style={{ 
                    padding: 'var(--spacing-md)',
                    marginBottom: 'var(--spacing-lg)',
                    borderRadius: 'var(--radius-sm)',
                    background: message.type === 'error' ? 'rgba(255,51,102,0.1)' : 'rgba(0,255,136,0.1)',
                    border: `1px solid ${message.type === 'error' ? 'var(--neon-red)' : 'var(--neon-green)'}`,
                    color: message.type === 'error' ? 'var(--neon-red)' : 'var(--neon-green)'
                  }}>
                    {message.text}
                  </div>
                )}
                <form onSubmit={handlePasswordChange}>
                  <div className="form-group">
                    <label className="form-label">Current Password</label>
                    <input
                      type="password"
                      className="form-input"
                      value={passwordForm.current_password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">New Password</label>
                    <input
                      type="password"
                      className="form-input"
                      value={passwordForm.new_password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
                      required
                      minLength={8}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Confirm New Password</label>
                    <input
                      type="password"
                      className="form-input"
                      value={passwordForm.confirm_password}
                      onChange={(e) => setPasswordForm({ ...passwordForm, confirm_password: e.target.value })}
                      required
                    />
                  </div>
                  <button type="submit" className="btn btn-primary">
                    Update Password
                  </button>
                </form>
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Two-Factor Authentication</h3>
              </div>
              <div className="card-body">
                <p style={{ marginBottom: 'var(--spacing-lg)', color: 'var(--text-secondary)' }}>
                  Add an extra layer of security to your account by enabling two-factor authentication.
                </p>
                <button className="btn btn-secondary" onClick={handleToggleMFA}>
                  {user?.mfa_enabled ? 'Disable MFA' : 'Enable MFA'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Users Tab (Admin Only) */}
        {activeTab === 'users' && user?.role === 'admin' && (
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">User Management</h3>
              <button className="btn btn-primary btn-sm" onClick={() => setShowAddUser(true)}>
                + Add User
              </button>
            </div>
            <div className="card-body" style={{ padding: 0 }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Email</th>
                    <th>Role</th>
                    <th>MFA</th>
                    <th>Status</th>
                    <th>Last Login</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <tr key={u.id}>
                      <td>{u.username}</td>
                      <td>{u.email}</td>
                      <td style={{ textTransform: 'capitalize' }}>{u.role}</td>
                      <td>
                        <span style={{ color: u.mfa_enabled ? 'var(--neon-green)' : 'var(--text-muted)' }}>
                          {u.mfa_enabled ? '✓' : '✗'}
                        </span>
                      </td>
                      <td>
                        <span style={{ color: u.is_active ? 'var(--neon-green)' : 'var(--neon-red)' }}>
                          {u.is_active ? 'Active' : 'Disabled'}
                        </span>
                      </td>
                      <td>
                        {u.last_login ? new Date(u.last_login).toLocaleDateString() : 'Never'}
                      </td>
                      <td>
                        <button 
                          className="btn btn-sm btn-ghost"
                          onClick={() => handleEditUser(u)}
                        >
                          Edit
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* About Tab */}
        {activeTab === 'about' && (
          <div className="card" style={{ maxWidth: '600px' }}>
            <div className="card-header">
              <h3 className="card-title">About HiveMind Deception Platform</h3>
            </div>
            <div className="card-body">
              <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-xl)' }}>
                <div style={{ fontSize: '64px', marginBottom: 'var(--spacing-md)' }}>🐝</div>
                <h2 style={{ 
                  fontFamily: 'var(--font-display)',
                  background: 'linear-gradient(135deg, var(--neon-green), var(--neon-cyan))',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  marginBottom: 'var(--spacing-sm)'
                }}>
                  HIVEMIND DECEPTION PLATFORM
                </h2>
                <p style={{ color: 'var(--text-muted)' }}>HDP v1.0.0</p>
              </div>

              <div style={{ marginBottom: 'var(--spacing-xl)' }}>
                <h4 style={{ color: 'var(--neon-cyan)', marginBottom: 'var(--spacing-sm)' }}>Description</h4>
                <p style={{ color: 'var(--text-secondary)' }}>
                  HiveMind Deception Platform is a controlled adversary engagement and evidence
                  management platform for lawful scam, fraud, phishing, and threat actor investigations.
                  HDP helps investigators manage cases, controlled personas, decoy assets, actor
                  interactions, evidence intake, indicators, enrichment workflows, and client-ready reports.
                </p>
              </div>

              <div style={{ 
                padding: 'var(--spacing-lg)',
                background: 'rgba(255, 136, 0, 0.1)',
                border: '1px solid var(--neon-orange)',
                borderRadius: 'var(--radius-md)',
                marginBottom: 'var(--spacing-xl)'
              }}>
                <h4 style={{ color: 'var(--neon-orange)', marginBottom: 'var(--spacing-sm)' }}>
                  ⚠️ Safety & Legal Disclaimer
                </h4>
                <p style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>
                  HDP is not an offensive security platform. It must be used only for lawful,
                  defensive, investigative, and intelligence workflows with proper authorization.
                  Do not use HDP for unauthorized access, malware deployment, credential theft,
                  hacking back, or impersonation of real private individuals.
                </p>
              </div>

              <div>
                <h4 style={{ color: 'var(--neon-cyan)', marginBottom: 'var(--spacing-sm)' }}>Credits</h4>
                <p style={{ color: 'var(--text-secondary)' }}>
                  Built with Flask, React, PostgreSQL, Redis, and WebSockets for Cipher Cortex /
                  Intercept Cell investigation workflows.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Add User Modal */}
      {showAddUser && (
        <div className="modal-overlay" onClick={() => setShowAddUser(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Create New User</h3>
              <button className="modal-close" onClick={() => setShowAddUser(false)}>×</button>
            </div>
            <form onSubmit={handleAddUser}>
              <div className="modal-body">
                {userError && (
                  <div style={{ 
                    padding: 'var(--spacing-md)',
                    marginBottom: 'var(--spacing-lg)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'rgba(255,51,102,0.1)',
                    border: '1px solid var(--neon-red)',
                    color: 'var(--neon-red)'
                  }}>
                    {userError}
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Username</label>
                  <input
                    type="text"
                    className="form-input"
                    value={newUserForm.username}
                    onChange={(e) => setNewUserForm({ ...newUserForm, username: e.target.value })}
                    required
                    autoFocus
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-input"
                    value={newUserForm.email}
                    onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Password</label>
                  <input
                    type="password"
                    className="form-input"
                    value={newUserForm.password}
                    onChange={(e) => setNewUserForm({ ...newUserForm, password: e.target.value })}
                    required
                    minLength={8}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <select
                    className="form-select"
                    value={newUserForm.role}
                    onChange={(e) => setNewUserForm({ ...newUserForm, role: e.target.value })}
                  >
                    <option value="viewer">Viewer</option>
                    <option value="operator">Operator</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-ghost" onClick={() => setShowAddUser(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary">
                  Create User
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="modal-overlay" onClick={() => setEditingUser(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Edit User: {editingUser.username}</h3>
              <button className="modal-close" onClick={() => setEditingUser(null)}>×</button>
            </div>
            <form onSubmit={handleUpdateUser}>
              <div className="modal-body">
                {userError && (
                  <div style={{ 
                    padding: 'var(--spacing-md)',
                    marginBottom: 'var(--spacing-lg)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'rgba(255,51,102,0.1)',
                    border: '1px solid var(--neon-red)',
                    color: 'var(--neon-red)'
                  }}>
                    {userError}
                  </div>
                )}
                <div className="form-group">
                  <label className="form-label">Username</label>
                  <input
                    type="text"
                    className="form-input"
                    value={editingUser.username}
                    disabled
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-input"
                    value={editUserForm.email}
                    onChange={(e) => setEditUserForm({ ...editUserForm, email: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Role</label>
                  <select
                    className="form-select"
                    value={editUserForm.role}
                    onChange={(e) => setEditUserForm({ ...editUserForm, role: e.target.value })}
                  >
                    <option value="viewer">Viewer</option>
                    <option value="operator">Operator</option>
                    <option value="admin">Admin</option>
                  </select>
                </div>
                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={editUserForm.is_active}
                      onChange={(e) => setEditUserForm({ ...editUserForm, is_active: e.target.checked })}
                      style={{ width: '18px', height: '18px' }}
                    />
                    <span className="form-label" style={{ margin: 0 }}>Account Active</span>
                  </label>
                </div>
              </div>
              <div className="modal-footer" style={{ justifyContent: 'space-between' }}>
                <button 
                  type="button" 
                  className="btn btn-danger"
                  onClick={handleDeleteUser}
                  style={{ 
                    background: 'rgba(255,51,102,0.15)',
                    border: '1px solid var(--neon-red)',
                    color: 'var(--neon-red)'
                  }}
                >
                  Delete User
                </button>
                <div style={{ display: 'flex', gap: 'var(--spacing-sm)' }}>
                  <button type="button" className="btn btn-ghost" onClick={() => setEditingUser(null)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary">
                    Save Changes
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default Settings;
